import React, { useEffect, useRef, useState } from 'react';

interface SignaturePadProps {
  /** Called with the drawn signature as a PNG blob. */
  onConfirm: (blob: Blob) => void;
  onCancel: () => void;
  confirmLabel: string;
  clearLabel: string;
  cancelLabel: string;
  /** Disables the buttons while the parent is uploading. */
  loading?: boolean;
}

/**
 * Canvas signature pad: draw with finger or mouse, clear, confirm.
 * Exports a white-background PNG. Pointer events cover touch + mouse;
 * touch-action none stops the page scrolling while signing.
 */
const SignaturePad: React.FC<SignaturePadProps> = ({
  onConfirm,
  onCancel,
  confirmLabel,
  clearLabel,
  cancelLabel,
  loading = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Size the canvas to its container once mounted (devicePixelRatio-aware so
  // strokes stay crisp on phone screens).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const pointerPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const { x, y } = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // A dot for single taps so even the shortest touch leaves a mark.
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    setHasDrawn(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointerPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasDrawn(false);
  };

  const handleConfirm = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn) return;
    canvas.toBlob((blob) => {
      if (blob) onConfirm(blob);
    }, 'image/png');
  };

  return (
    <div className="w-full">
      <div className="border-2 border-dashed border-gray-300 rounded-2xl overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          className="w-full h-48 block cursor-crosshair"
          style={{ touchAction: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
      </div>
      <div className="flex gap-3 mt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-3 rounded-2xl text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={handleClear}
          disabled={loading || !hasDrawn}
          className="px-4 py-3 rounded-2xl text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-40"
        >
          {clearLabel}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading || !hasDrawn}
          className={`flex-1 px-4 py-3 rounded-2xl text-white text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
            loading || !hasDrawn ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90 shadow-md'
          }`}
          style={{ backgroundColor: '#2E5D7D' }}
        >
          {loading && (
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          )}
          {confirmLabel}
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
